import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { crmAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  ArrowLeft, MagnifyingGlass, PaperPlaneTilt, Tag, DownloadSimple,
  ChatCircleDots, UsersThree, Megaphone, GearSix, Plus, Trash, Note, X
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VoiceInputButton } from '../components/VoiceInputButton';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const TABS = [
  { id: 'contacts', label: 'Contacts', icon: UsersThree },
  { id: 'messages', label: 'Messages', icon: ChatCircleDots },
  { id: 'segments', label: 'Segments', icon: Tag },
  { id: 'settings', label: 'Settings', icon: GearSix },
];

export default function CRMPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('contacts');

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="crm-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" /></Link>
            <span className="text-[#666666] hidden sm:inline">/</span>
            <span className="text-white font-semibold text-sm hidden sm:inline">CRM</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/seller-analytics"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm">Analytics</Button></Link>
            <Link to="/dashboard"><Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm"><ArrowLeft className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Dashboard</span></Button></Link>
          </div>
        </div>
      </header>

      {/* Tab bar */}
      <div className="border-b border-[#222222]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === id ? 'border-[#22C55E] text-[#22C55E]' : 'border-transparent text-[#666666] hover:text-white'
              }`}
              data-testid={`tab-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {tab === 'contacts' && <ContactsTab />}
        {tab === 'messages' && <MessagesTab />}
        {tab === 'segments' && <SegmentsTab />}
        {tab === 'settings' && <SettingsTab />}
      </main>
    </div>
  );
}

// ==================== CONTACTS TAB ====================
function ContactsTab() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all');
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteTags, setNoteTags] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadContacts(); loadSegments(); }, []);

  const loadContacts = async (s, seg) => {
    setLoading(true);
    try {
      const params = {};
      if (s || search) params.search = s || search;
      if ((seg || segment) !== 'all') params.segment = seg || segment;
      const { data } = await crmAPI.getContacts(params);
      setContacts(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const loadSegments = async () => {
    try { const { data } = await crmAPI.getSegments(); setSegments(data); } catch (err) { /* silent */ }
  };

  const handleSearch = (e) => { e.preventDefault(); loadContacts(search, segment); };

  const handleExport = () => { window.open(crmAPI.exportCsv(), '_blank'); };

  const openNoteDialog = (c) => {
    setNoteDialog(c);
    setNoteText(c.notes || '');
    setNoteTags((c.tags || []).join(', '));
  };

  const saveNotes = async () => {
    if (!noteDialog) return;
    try {
      const tags = noteTags.split(',').map(t => t.trim()).filter(Boolean);
      await crmAPI.updateNotes(noteDialog.user_id, noteText, tags);
      toast.success('Notes saved');
      setNoteDialog(null);
      loadContacts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts..." className="pl-9 bg-[#1A1A1A] border-[#333333] text-white rounded-lg h-10" data-testid="crm-search" />
          </div>
          <Button type="submit" className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg h-10">Search</Button>
        </form>
        <Select value={segment} onValueChange={(v) => { setSegment(v); loadContacts(search, v); }}>
          <SelectTrigger className="w-40 bg-[#1A1A1A] border-[#333333] text-white rounded-lg h-10" data-testid="segment-filter">
            <SelectValue placeholder="All Contacts" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#333333]">
            <SelectItem value="all">All Contacts</SelectItem>
            {segments.map(s => <SelectItem key={s.segment_id} value={s.segment_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={handleExport} variant="outline" className="border-[#333333] text-white hover:bg-[#333333] rounded-lg h-10" data-testid="export-csv">
          <DownloadSimple className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#666666]">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="p-8 text-center text-[#666666]">No contacts yet. They'll appear after buyers/followers interact with you.</div>
          ) : (
            <div className="divide-y divide-[#1A1A1A]">
              {contacts.map((c) => (
                <div key={c.user_id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#151515] transition-colors" data-testid={`contact-${c.user_id}`}>
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#333333] flex items-center justify-center text-white text-sm font-bold shrink-0">{c.name?.[0]}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white truncate">{c.name}</p>
                      {c.is_customer && <span className="text-[8px] bg-[#22C55E]/10 text-[#22C55E] px-1.5 py-0.5 rounded font-bold">BUYER</span>}
                      {c.is_follower && <span className="text-[8px] bg-[#22C55E]/10 text-[#22C55E] px-1.5 py-0.5 rounded font-bold">FOLLOWER</span>}
                      {(c.tags || []).map(t => (
                        <span key={t} className="text-[8px] bg-[#F59E0B]/10 text-[#F59E0B] px-1.5 py-0.5 rounded font-bold">{t}</span>
                      ))}
                    </div>
                    <p className="text-xs text-[#666666]">{c.email} {c.username ? `· $${c.username}` : ''}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-4 text-xs text-[#666666] shrink-0">
                    <span>{fmt(c.total_spent)} spent</span>
                    <span>{c.purchase_count} orders</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openNoteDialog(c)} className="text-[#999999] hover:text-white h-8 w-8 p-0" title="Notes & Tags" data-testid={`note-btn-${c.user_id}`}>
                    <Note className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!noteDialog} onOpenChange={() => setNoteDialog(null)}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-white">Notes & Tags — {noteDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-[#999999]">Private Notes</Label>
              <div className="relative">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add private notes about this contact..." className="bg-[#111111] border-[#333333] text-white mt-1 min-h-[80px]" data-testid="note-text" />
                <div className="absolute bottom-2 right-2">
                  <VoiceInputButton onTranscript={(text) => setNoteText(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </div>
            <div><Label className="text-xs text-[#999999]">Tags (comma-separated)</Label>
              <Input value={noteTags} onChange={(e) => setNoteTags(e.target.value)} placeholder="VIP, wholesale, priority" className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="note-tags" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(null)} className="border-[#333333] text-white">Cancel</Button>
            <Button onClick={saveNotes} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold" data-testid="save-notes">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== MESSAGES TAB ====================
function MessagesTab() {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [newDialog, setNewDialog] = useState(false);
  const [broadcastDialog, setBroadcastDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [newBuyerId, setNewBuyerId] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [bcSubject, setBcSubject] = useState('');
  const [bcMessage, setBcMessage] = useState('');
  const [segments, setSegments] = useState([]);
  const [bcSegment, setBcSegment] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThreads(); }, []);

  const loadThreads = async () => {
    try { const { data } = await crmAPI.getThreads(); setThreads(data); } catch (err) { /* silent */ }
  };

  const openThread = async (threadId) => {
    setActiveThread(threadId);
    try { const { data } = await crmAPI.getThread(threadId); setThreadData(data); } catch (err) { /* silent */ }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !activeThread) return;
    try {
      await crmAPI.replyToThread(activeThread, replyText);
      setReplyText('');
      openThread(activeThread);
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openNewMessage = async () => {
    try { const { data } = await crmAPI.getContacts({}); setContacts(data); } catch (err) { /* silent */ }
    setNewDialog(true);
  };

  const handleNewThread = async () => {
    if (!newBuyerId || !newMessage.trim()) return;
    try {
      await crmAPI.createThread(newBuyerId, newSubject, newMessage);
      toast.success('Message sent');
      setNewDialog(false);
      setNewBuyerId('');
      setNewSubject('');
      setNewMessage('');
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openBroadcast = async () => {
    try { const { data } = await crmAPI.getSegments(); setSegments(data); } catch (err) { /* silent */ }
    setBroadcastDialog(true);
  };

  const handleBroadcast = async () => {
    if (!bcMessage.trim()) return;
    try {
      const { data } = await crmAPI.broadcast(bcSubject, bcMessage, bcSegment || undefined);
      toast.success(data.message);
      setBroadcastDialog(false);
      setBcSubject('');
      setBcMessage('');
      setBcSegment('');
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <>
      <div className="flex gap-2 mb-4">
        <Button onClick={openNewMessage} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg h-10" data-testid="new-message-btn">
          <PaperPlaneTilt className="h-4 w-4 mr-2" /> New Message
        </Button>
        <Button onClick={openBroadcast} variant="outline" className="border-[#333333] text-white hover:bg-[#333333] rounded-lg h-10" data-testid="broadcast-btn">
          <Megaphone className="h-4 w-4 mr-2" /> Broadcast
        </Button>
      </div>

      <div className="grid md:grid-cols-5 gap-4">
        {/* Thread List */}
        <Card className="md:col-span-2 bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden">
          <CardContent className="p-0 max-h-[500px] overflow-y-auto">
            {threads.length === 0 ? (
              <div className="p-6 text-center text-[#666666] text-sm">No messages yet</div>
            ) : threads.map(t => (
              <div
                key={t.thread_id}
                onClick={() => openThread(t.thread_id)}
                className={`px-4 py-3 cursor-pointer border-b border-[#1A1A1A] transition-colors ${activeThread === t.thread_id ? 'bg-[#22C55E]/5 border-l-2 border-l-[#22C55E]' : 'hover:bg-[#151515]'}`}
                data-testid={`thread-${t.thread_id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-white truncate">{t.buyer?.name || 'Unknown'}</p>
                  <span className="text-[10px] text-[#555555] shrink-0">{timeAgo(t.updated_at)}</span>
                </div>
                <p className="text-xs text-[#22C55E] font-medium truncate">{t.subject}</p>
                <p className="text-xs text-[#666666] truncate mt-0.5">{t.last_message}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Thread Detail */}
        <Card className="md:col-span-3 bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden">
          {!threadData ? (
            <CardContent className="p-8 text-center text-[#666666] text-sm">Select a conversation</CardContent>
          ) : (
            <>
              <CardHeader className="border-b border-[#222222] py-3 px-4">
                <div className="flex items-center gap-3">
                  {threadData.buyer?.avatar_url ? (
                    <img src={threadData.buyer.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#333333] flex items-center justify-center text-white text-xs font-bold">{threadData.buyer?.name?.[0]}</div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">{threadData.buyer?.name}</p>
                    <p className="text-xs text-[#666666]">{threadData.thread.subject}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 max-h-[320px] overflow-y-auto space-y-3">
                {threadData.messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.sender_type === 'vendor' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 ${m.sender_type === 'vendor' ? 'bg-[#22C55E]/10 text-white' : 'bg-[#222222] text-white'}`}>
                      <p className="text-sm">{m.content}</p>
                      <p className="text-[10px] text-[#555555] mt-1">{timeAgo(m.created_at)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
              <div className="border-t border-[#222222] p-3 flex gap-2">
                <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Type a reply..." className="bg-[#111111] border-[#333333] text-white rounded-lg flex-1" data-testid="reply-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }}} />
                <VoiceInputButton onTranscript={(text) => setReplyText(prev => prev ? prev + ' ' + text : text)} />
                <Button onClick={handleReply} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black rounded-lg shrink-0" data-testid="send-reply">
                  <PaperPlaneTilt className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* New Message Dialog */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">New Message</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-[#999999]">To</Label>
              <Select value={newBuyerId} onValueChange={setNewBuyerId}>
                <SelectTrigger className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="msg-recipient"><SelectValue placeholder="Select contact..." /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333333] max-h-48">
                  {contacts.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name} ({c.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#999999]">Subject</Label>
              <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Subject line..." className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="msg-subject" />
            </div>
            <div><Label className="text-xs text-[#999999]">Message</Label>
              <div className="relative">
                <Textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Your message..." className="bg-[#111111] border-[#333333] text-white mt-1 min-h-[80px]" data-testid="msg-body" />
                <div className="absolute bottom-2 right-2">
                  <VoiceInputButton onTranscript={(text) => setNewMessage(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)} className="border-[#333333] text-white">Cancel</Button>
            <Button onClick={handleNewThread} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold" data-testid="send-message">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastDialog} onOpenChange={setBroadcastDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-md">
          <DialogHeader><DialogTitle className="text-white">Broadcast Message</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-[#999999]">Target Segment (optional)</Label>
              <Select value={bcSegment} onValueChange={setBcSegment}>
                <SelectTrigger className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="bc-segment"><SelectValue placeholder="All contacts" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#333333]">
                  <SelectItem value=" ">All Contacts</SelectItem>
                  {segments.map(s => <SelectItem key={s.segment_id} value={s.segment_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#999999]">Subject</Label>
              <Input value={bcSubject} onChange={(e) => setBcSubject(e.target.value)} placeholder="Announcement subject..." className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="bc-subject" />
            </div>
            <div><Label className="text-xs text-[#999999]">Message</Label>
              <div className="relative">
                <Textarea value={bcMessage} onChange={(e) => setBcMessage(e.target.value)} placeholder="Your broadcast message..." className="bg-[#111111] border-[#333333] text-white mt-1 min-h-[80px]" data-testid="bc-message" />
                <div className="absolute bottom-2 right-2">
                  <VoiceInputButton onTranscript={(text) => setBcMessage(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcastDialog(false)} className="border-[#333333] text-white">Cancel</Button>
            <Button onClick={handleBroadcast} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold" data-testid="send-broadcast">Send Broadcast</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== SEGMENTS TAB ====================
function SegmentsTab() {
  const [segments, setSegments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editSeg, setEditSeg] = useState(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([crmAPI.getSegments(), crmAPI.getContacts({})]);
      setSegments(s.data);
      setContacts(c.data);
    } catch (err) { /* silent */ }
  };

  const openCreate = () => { setEditSeg(null); setName(''); setDesc(''); setSelectedIds([]); setDialog(true); };

  const openEdit = (seg) => { setEditSeg(seg); setName(seg.name); setDesc(seg.description || ''); setSelectedIds(seg.member_ids || []); setDialog(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      if (editSeg) {
        await crmAPI.updateSegment(editSeg.segment_id, { name, description: desc, member_ids: selectedIds });
        toast.success('Segment updated');
      } else {
        await crmAPI.createSegment({ name, description: desc, member_ids: selectedIds });
        toast.success('Segment created');
      }
      setDialog(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this segment?')) return;
    try { await crmAPI.deleteSegment(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleMember = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#999999]">{segments.length} segment{segments.length !== 1 ? 's' : ''}</p>
        <Button onClick={openCreate} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg h-10" data-testid="create-segment">
          <Plus className="h-4 w-4 mr-2" /> New Segment
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {segments.map(seg => (
          <Card key={seg.segment_id} className="bg-[#1A1A1A] border-[#222222] rounded-lg" data-testid={`segment-${seg.segment_id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">{seg.name}</p>
                  {seg.description && <p className="text-xs text-[#666666] mt-0.5">{seg.description}</p>}
                  <p className="text-xs text-[#22C55E] mt-1">{(seg.member_ids || []).length} members</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(seg)} className="text-[#999999] hover:text-white h-8 w-8 p-0">
                    <GearSix className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(seg.segment_id)} className="text-[#999999] hover:text-red-400 h-8 w-8 p-0">
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#333333] text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editSeg ? 'Edit' : 'Create'} Segment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs text-[#999999]">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="seg-name" />
            </div>
            <div><Label className="text-xs text-[#999999]">Description</Label>
              <Input value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="seg-desc" />
            </div>
            <div><Label className="text-xs text-[#999999]">Members ({selectedIds.length} selected)</Label>
              <div className="mt-1 max-h-40 overflow-y-auto border border-[#333333] rounded-lg">
                {contacts.map(c => (
                  <label key={c.user_id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#222222] cursor-pointer">
                    <input type="checkbox" checked={selectedIds.includes(c.user_id)} onChange={() => toggleMember(c.user_id)} className="accent-[#22C55E]" />
                    <span className="text-sm text-white">{c.name}</span>
                    <span className="text-xs text-[#666666]">{c.email}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-[#333333] text-white">Cancel</Button>
            <Button onClick={handleSave} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold" data-testid="save-segment">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ==================== SETTINGS TAB ====================
function SettingsTab() {
  const [settings, setSettings] = useState({ enabled: false, delay_hours: 24, message: '', subject: '' });
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const { data } = await crmAPI.getFollowup(); setSettings(data); } catch (err) { /* silent */ }
    setLoading(false);
  };

  const save = async () => {
    try {
      await crmAPI.updateFollowup(settings);
      toast.success('Auto follow-up settings saved');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg max-w-lg">
      <CardHeader className="border-b border-[#222222]">
        <CardTitle className="text-sm font-semibold text-white">Auto Follow-Up After Purchase</CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {loading ? <div className="text-[#666666]">Loading...</div> : (
          <>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-white">Enable Auto Follow-Up</Label>
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))} data-testid="followup-toggle" />
            </div>
            <div><Label className="text-xs text-[#999999]">Delay (hours after purchase)</Label>
              <Input type="number" value={settings.delay_hours} onChange={(e) => setSettings(s => ({ ...s, delay_hours: parseInt(e.target.value) || 24 }))}
                className="bg-[#111111] border-[#333333] text-white mt-1 w-24" data-testid="followup-delay" />
            </div>
            <div><Label className="text-xs text-[#999999]">Subject</Label>
              <Input value={settings.subject || ''} onChange={(e) => setSettings(s => ({ ...s, subject: e.target.value }))}
                placeholder="Thank you for your purchase!" className="bg-[#111111] border-[#333333] text-white mt-1" data-testid="followup-subject" />
            </div>
            <div><Label className="text-xs text-[#999999]">Message Template</Label>
              <Textarea value={settings.message || ''} onChange={(e) => setSettings(s => ({ ...s, message: e.target.value }))}
                placeholder="Thank you for your purchase! We hope you enjoy..." className="bg-[#111111] border-[#333333] text-white mt-1 min-h-[100px]" data-testid="followup-message" />
            </div>
            <Button onClick={save} className="bg-[#22C55E] hover:bg-[#1EA34B] text-black font-semibold rounded-lg" data-testid="save-followup">Save Settings</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
